// src/components/DownloadModal.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { TbCrown, TbDownload, TbCheck, TbX, TbShieldCheck, TbSparkles } from 'react-icons/tb';
import axios from 'axios';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { 
  detectUserCountry, 
  getCurrencyForCountry,
  getPriceConfig,
  getPaymentProcessorForCurrency
} from '../util/domain-utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const DownloadModal = ({ 
  isOpen, 
  closeModal, 
  selectedTemplate, 
  onDownload, 
  freeTemplates = ['Professional', 'Classic', 'Dynamic', 'Modern']
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [currency, setCurrency] = useState('inr');
  const [paymentProcessor, setPaymentProcessor] = useState('razorpay');
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [razorpayReady, setRazorpayReady] = useState(false);

  // Check if selected template is in the free templates list
  const isTemplateAvailable = freeTemplates.includes(selectedTemplate);

  // Detect country & currency
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const countryCode = await detectUserCountry();
        const detectedCurrency = getCurrencyForCountry(countryCode);
        setCurrency(detectedCurrency);
        setPaymentProcessor(getPaymentProcessorForCurrency(detectedCurrency));
      } catch (err) {
        console.error('Error detecting country in DownloadModal:', err);
        setCurrency('inr');
        setPaymentProcessor('razorpay');
      }
    };
    detectCountry();
  }, []);

  // Load Razorpay script if INR
  useEffect(() => {
    if (currency.toLowerCase() === 'inr') {
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => setRazorpayReady(true);
        script.onerror = () => console.error('Failed to load Razorpay script');
        document.body.appendChild(script);
      } else {
        setRazorpayReady(true);
      }
    }
  }, [currency]);

  // Razorpay Checkout
  const handleRazorpayCheckout = async (planId) => {
    try {
      setLoadingPlan(planId);
      setErrorMessage('');

      if (!user || !user.email) {
        localStorage.setItem('selectedPlan', planId);
        localStorage.setItem('selectedCurrency', currency);
        localStorage.setItem('returnUrl', window.location.pathname);
        closeModal();
        navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`, {
          state: { from: window.location.pathname, message: 'Please sign in to complete your upgrade' }
        });
        return;
      }

      const userId = user.uid;
      const response = await axios.post(`${BACKEND_URL}/api/create-razorpay-order`, {
        planId,
        userId,
        email: user.email,
        name: user.displayName || user.email
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create payment order');
      }

      const { order, key_id, user: userData, config } = response.data;

      const options = {
        key: key_id,
        amount: order.amount,
        currency: order.currency,
        name: config?.display?.name || 'ResumeDesign.AI',
        description: config?.display?.description || 'Premium Plan Access',
        image: config?.display?.logo,
        order_id: order.id,
        handler: async function(res) {
          try {
            const verifyResponse = await axios.post(`${BACKEND_URL}/api/verify-razorpay-payment`, {
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_signature: res.razorpay_signature,
              userId,
              planId,
              email: user.email
            });

            if (verifyResponse.data.success) {
              closeModal();
              navigate(`/success?payment_id=${res.razorpay_payment_id}`);
            } else {
              throw new Error(verifyResponse.data.message || 'Payment verification failed');
            }
          } catch (err) {
            console.error('Error verifying payment:', err);
            setErrorMessage(err.message || 'Payment verification failed');
          } finally {
            setLoadingPlan(null);
          }
        },
        prefill: {
          name: userData?.name || user.displayName || '',
          email: userData?.email || user.email,
          contact: userData?.contact || ''
        },
        notes: { userId, planId },
        theme: { color: config?.theme?.color || '#2563eb' },
        modal: {
          ondismiss: function() {
            setLoadingPlan(null);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      razorpay.on('payment.failed', function(res) {
        setErrorMessage(res.error?.description || 'Payment failed');
        setLoadingPlan(null);
      });
    } catch (err) {
      console.error('Razorpay checkout error:', err);
      setErrorMessage(err.message || 'Payment processing error');
      setLoadingPlan(null);
    }
  };

  // Stripe Checkout
  const handleStripeCheckout = async (planId) => {
    try {
      setLoadingPlan(planId);
      setErrorMessage('');

      if (!user || !user.email) {
        localStorage.setItem('selectedPlan', planId);
        localStorage.setItem('selectedCurrency', currency);
        localStorage.setItem('returnUrl', window.location.pathname);
        closeModal();
        navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`, {
          state: { from: window.location.pathname, message: 'Please sign in to complete your upgrade' }
        });
        return;
      }

      const userId = user.uid;
      let customerId;
      const userDocRef = doc(db, 'users', user.email);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists() && userDoc.data().customerId) {
        customerId = userDoc.data().customerId;
      } else {
        const createCustomerResponse = await axios.post(`${BACKEND_URL}/api/create-customer`, {
          email: user.email,
          name: user.displayName || user.email,
          userId
        });
        if (createCustomerResponse.data.success) {
          customerId = createCustomerResponse.data.customerId;
          await setDoc(userDocRef, {
            email: user.email,
            customerId,
            userId,
            createdAt: new Date()
          }, { merge: true });
        } else {
          throw new Error('Failed to create customer');
        }
      }

      const response = await axios.post(`${BACKEND_URL}/api/create-checkout-session`, {
        planId,
        customerId,
        currency,
        email: user.email,
        userId
      });

      if (response.data.success) {
        await setDoc(userDocRef, {
          pendingSubscription: true,
          pendingPlanType: planId,
          userId,
          pendingSessionId: response.data.sessionId,
          updatedAt: new Date()
        }, { merge: true });

        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Stripe checkout error:', err);
      setErrorMessage(err.message || 'Payment processing error');
      setLoadingPlan(null);
    }
  };

  const handleSelectPlan = (planId) => {
    if (paymentProcessor === 'razorpay') {
      handleRazorpayCheckout(planId);
    } else {
      handleStripeCheckout(planId);
    }
  };

  // Plan items definition
  const modalPlans = [
    {
      id: 'sevenDays',
      name: '7 Days',
      price: getPriceConfig('sevenDays', currency).formatted,
      subtitle: 'One-time payment • 7 days access',
      features: ['Unlimited PDF downloads', 'All templates & colors', 'No watermarks'],
      buttonText: 'Get 7 Days',
      popular: false
    },
    {
      id: 'oneMonth',
      name: '1 Month',
      price: getPriceConfig('oneMonth', currency).formatted,
      subtitle: 'One-time payment • 30 days access',
      features: ['Unlimited PDF downloads', 'AI resume optimization', 'Job tracking tools'],
      buttonText: 'Get 1 Month',
      popular: false
    },
    {
      id: 'lifetime',
      name: 'Lifetime',
      price: getPriceConfig('lifetime', currency).formatted,
      subtitle: 'Pay once • Keep forever',
      features: ['Unlimited forever access', 'All current & future templates', 'AI generations & priority support'],
      buttonText: 'Get Lifetime Access',
      popular: true,
      badge: 'MOST POPULAR'
    }
  ];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={`w-full ${isTemplateAvailable ? 'max-w-md' : 'max-w-4xl'} transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-2xl transition-all border border-gray-100`}>
                {isTemplateAvailable ? (
                  // Free Template Download Confirmation
                  <>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Dialog.Title as="h3" className="text-xl font-bold leading-6 text-gray-900 flex items-center">
                          <TbDownload className="mr-2 text-blue-600 text-2xl" />
                          Download Your Document
                        </Dialog.Title>
                        <button 
                          onClick={closeModal}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
                        >
                          <TbX size={20} />
                        </button>
                      </div>
                      
                      <p className="text-gray-600 mb-6">
                        You're downloading the <span className="font-semibold text-gray-900">{selectedTemplate}</span> template.
                      </p>
                      
                      <div className="flex space-x-3 justify-end">
                        <button
                          type="button"
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none"
                          onClick={closeModal}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm focus:outline-none"
                          onClick={onDownload}
                        >
                          Download Now
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  // Premium Template: Directly Display Plans
                  <>
                    {/* Header Banner */}
                    <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-800 text-white p-6 pb-7">
                      <button 
                        onClick={closeModal}
                        className="absolute top-4 right-4 text-white/80 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors"
                        aria-label="Close"
                      >
                        <TbX size={22} />
                      </button>

                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center text-indigo-950 shadow-md">
                          <TbCrown size={22} />
                        </div>
                        <div>
                          <span className="text-xs uppercase tracking-wider font-semibold text-yellow-300">Premium Template</span>
                          <h3 className="text-2xl font-bold text-white">
                            Unlock {selectedTemplate || 'Premium'} & All Pro Designs
                          </h3>
                        </div>
                      </div>
                      <p className="text-indigo-100 text-sm max-w-2xl mt-1">
                        Choose a plan below for instant access to download your resume in ATS-optimized premium formats.
                      </p>

                      {/* Benefits chips */}
                      <div className="flex flex-wrap gap-2 mt-4 text-xs font-medium text-white">
                        <span className="bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1.5">
                          <TbCheck className="text-green-300" /> Unlimited PDF Downloads
                        </span>
                        <span className="bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1.5">
                          <TbCheck className="text-green-300" /> 22+ ATS Pro Templates
                        </span>
                        <span className="bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1.5">
                          <TbCheck className="text-green-300" /> AI Resume Optimization
                        </span>
                        <span className="bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1.5">
                          <TbCheck className="text-green-300" /> No Watermarks
                        </span>
                      </div>
                    </div>

                    {/* Modal Body: Pricing Plans */}
                    <div className="p-6 md:p-8 bg-gray-50/50">
                      {errorMessage && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                          {errorMessage}
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {modalPlans.map((plan) => (
                          <div
                            key={plan.id}
                            className={`relative rounded-2xl bg-white p-5 flex flex-col justify-between transition-all duration-200 ${
                              plan.popular 
                                ? 'border-2 border-indigo-600 shadow-xl ring-2 ring-indigo-100' 
                                : 'border border-gray-200 hover:border-gray-300 hover:shadow-md'
                            }`}
                          >
                            {plan.popular && (
                              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-[11px] font-bold px-3 py-0.5 rounded-full shadow-sm uppercase tracking-wide">
                                {plan.badge}
                              </div>
                            )}

                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="text-lg font-bold text-gray-900">{plan.name}</h4>
                                {plan.popular && (
                                  <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                                    Best Value
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mb-3">{plan.subtitle}</p>

                              <div className="mb-4 pb-4 border-b border-gray-100">
                                <span className="text-3xl font-extrabold text-gray-900">{plan.price}</span>
                                <span className="text-xs text-gray-500 ml-1">one-time</span>
                              </div>

                              <ul className="space-y-2 mb-6">
                                {plan.features.map((feature, idx) => (
                                  <li key={idx} className="flex items-start text-xs text-gray-600">
                                    <TbCheck className="text-green-600 mr-2 mt-0.5 flex-shrink-0 text-sm" />
                                    <span>{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <button
                              onClick={() => handleSelectPlan(plan.id)}
                              disabled={loadingPlan !== null}
                              className={`w-full py-2.5 px-4 rounded-xl font-semibold text-sm transition-all shadow-sm flex items-center justify-center gap-2 ${
                                plan.popular
                                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 hover:shadow-md'
                                  : 'bg-gray-900 text-white hover:bg-gray-800'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {loadingPlan === plan.id ? (
                                <>
                                  <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24" fill="none">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                  </svg>
                                  <span>Processing...</span>
                                </>
                              ) : (
                                <>
                                  {plan.popular && <TbSparkles size={16} className="text-yellow-300" />}
                                  <span>{plan.buttonText}</span>
                                </>
                              )}
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Footer guarantee & security */}
                      <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
                        <div className="flex items-center gap-2">
                          <TbShieldCheck className="text-green-600 text-lg" />
                          <span>Guaranteed safe checkout • 7-day money-back guarantee • No hidden fees</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            closeModal();
                            navigate('/plans');
                          }}
                          className="text-indigo-600 hover:text-indigo-800 font-medium hover:underline"
                        >
                          Compare all features & plans →
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DownloadModal;